export const MENTION_REGEX = /<@([0-9a-f]{24})>/g

export function formatMentionMarkup(userId: string): string {
  return `<@${userId}>`
}

export function extractMentionIds(text: string): string[] {
  const ids = new Set<string>()
  for (const match of text.matchAll(MENTION_REGEX)) {
    ids.add(match[1])
  }
  return [...ids]
}

export function resolveMentions(
  text: string,
  resolveName: (id: string) => string | undefined,
): string {
  return text.replace(
    MENTION_REGEX,
    (_token, id) => `@${resolveName(id) || 'user'}`,
  )
}

export interface MentionSegment {
  readonly text: string
  readonly userId?: string
}

export function splitMentionSegments(text: string): MentionSegment[] {
  const segments: MentionSegment[] = []
  let lastIndex = 0

  for (const match of text.matchAll(MENTION_REGEX)) {
    if (match.index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.index) })
    }
    segments.push({ text: match[0], userId: match[1] })
    lastIndex = match.index + match[0].length
  }

  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex) })
  }

  return segments
}

export interface MessageSegment {
  readonly text: string
  readonly userId?: string
  readonly url?: string
}

const URL_PATTERN = /https?:\/\/\S+/g
// A URL at the end of a sentence should not swallow the punctuation:
// "see https://a.com." links to https://a.com
const TRAILING_PUNCTUATION_PATTERN = /[.,;:!?)\]]+$/

function splitUrlSegments(text: string): MessageSegment[] {
  const segments: MessageSegment[] = []
  let lastIndex = 0

  for (const match of text.matchAll(URL_PATTERN)) {
    const url = match[0].replace(TRAILING_PUNCTUATION_PATTERN, '')

    if (match.index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.index) })
    }
    segments.push({ text: url, url })
    lastIndex = match.index + url.length
  }

  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex) })
  }

  return segments
}

// The one owner of message-text segmentation: mention tokens first, then
// URLs inside the plain stretches between them.
export function splitMessageSegments(text: string): MessageSegment[] {
  const segments: MessageSegment[] = []

  for (const segment of splitMentionSegments(text)) {
    if (segment.userId) {
      segments.push(segment)
      continue
    }
    segments.push(...splitUrlSegments(segment.text))
  }

  return segments
}
